/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useMemo } from "react";
import { ResourcePathBreadcrumbs } from "../../../../../ui/components/ResourcePathBreadcrumbs";
import { Breadcrumbs } from "../../../../../ui/components/Breadcrumbs/Breadcrumbs";
import { IBreadcrumbsItem } from "../../../../../ui/components/Breadcrumbs/BreadcrumbsTypes";
import { ThemeEditor } from "../../ThemeEditor";
import { useResourceInfo, useStateWithEvent } from "../../../../../ui/hooks";
import { ThemeModel } from "../../../Common/ThemeModel";
import { isSelectionItemBlock, isSelectionItemStyle } from "../../../Common/ThemeEditSelection";
import {
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	isSchemaConstObjectModel,
	TGetSchemaConstArrayItemSchema,
	TGetSchemaConstObjectProps
} from "@adapptio/lib-blueprint";
import { TBlueprintThemeCustomCssSchema, TBlueprintThemeStyleSchema } from "@adapptio/lib-core";
import { ToolbarSpacer } from "../../../../../ui/components/Toolbar/ToolbarSpacer";

export interface IThemeEditorSecondaryToolbarProps {
	view: ThemeEditor;
	themeModel: ThemeModel;
}

export const ThemeEditorSecondaryToolbar: FC<IThemeEditorSecondaryToolbarProps> = ({ view, themeModel }) => {
	const resourceInfo = useResourceInfo(view);
	const selection = useMemo(() => themeModel.getSelection(), [ themeModel ]);

	const selectedItem = useStateWithEvent(selection.onChange, () => selection.getItems()[0]);

	const selectedNodeModel = useMemo(
		() => (selectedItem ? themeModel.getDesignContext().getNodeById(selectedItem.nodeId) : null),
		[ selectedItem ]
	);

	const selectedBlock = isSelectionItemBlock(selectedItem) ? selectedNodeModel : null;
	const selectedStyle = selectedBlock
		? selectedBlock?.parent?.parent
		: isSelectionItemStyle(selectedItem)
			? selectedItem?.mapEntry?.value
			: null;

	// Build selection path items for style/block selection
	const selectionPathItems: IBreadcrumbsItem[] = [];

	if (isSchemaConstObjectModel<TGetSchemaConstObjectProps<TBlueprintThemeStyleSchema>>(selectedStyle)) {
		let styleLabel = selectedStyle?.props?.label?.value;

		if (!styleLabel) {
			const stylesMap = selectedStyle?.parent;

			if (isSchemaConstMapModel(stylesMap)) {
				const mapEntry = stylesMap.items.find((item) => item.value?.nodeId === selectedStyle?.nodeId);

				if (isSchemaConstMapModelEntry(mapEntry)) {
					styleLabel = mapEntry.key.value;
				}
			}
		}

		if (styleLabel) {
			selectionPathItems.push({
				label: styleLabel
			});
		}
	}

	if (
		isSchemaConstObjectModel<
			TGetSchemaConstObjectProps<TGetSchemaConstArrayItemSchema<TBlueprintThemeCustomCssSchema>>
		>(selectedBlock)
	) {
		const blockName = selectedBlock.props.name.value;

		selectionPathItems.push({
			label: blockName
		});
	}

	return (
		<>
			<ResourcePathBreadcrumbs
				parentList={resourceInfo.parentList}
				resourceName={resourceInfo.resource?.descriptor?.name}
				resourceId={resourceInfo.resource?.id}
				maxVisibleCrumbs={12}
			/>
			{selectionPathItems.length > 0 && (
				<>
					<ToolbarSpacer grow={1} />
					<Breadcrumbs items={selectionPathItems} maxVisibleCrumbs={12} />
				</>
			)}
		</>
	);
};

